import { Send, CheckCheck, MessageSquareText, Wallet } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';
import type { SalesOrder } from '../../../types/salesOrder';
import { SalesOrderStatusBadge } from './SalesOrderStatusBadge';
import { SalesOrderPaymentBadge } from './SalesOrderPaymentBadge';
import type { SalesOrderRowActions } from './types';

type SalesOrderTableRowProps = { so: SalesOrder } & SalesOrderRowActions;

/** Desktop (table) presentation of a single sales order. */
export function SalesOrderTableRow({
  so,
  canWrite,
  currencySymbol,
  onOpen,
  onCustomerClick,
  onMarkAs,
  onPay,
}: SalesOrderTableRowProps) {
  const { t } = useLanguage();
  const paymentStatus = so.paymentStatus ?? 'unpaid';
  return (
    <tr data-test-id={`so-row-${so.id}`} onClick={() => onOpen(so)} className="hover:bg-gray-50 cursor-pointer">
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        <div className="flex items-center gap-2">
          <span data-test-id={`so-number-${so.id}`}>{so.orderNumber}</span>
          {so.internalNotes && (
            <div className="group relative">
              <MessageSquareText className="h-4 w-4 text-blue-500" />
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200">
                {so.internalNotes}
                <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
              </div>
            </div>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCustomerClick(so.customer);
          }}
          className="text-primary-600 hover:text-primary-800 hover:underline text-left"
        >
          {so.customer}
        </button>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(so.orderDate).toLocaleDateString()}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(so.totalAmount, currencySymbol)}</td>
      <td className="px-6 py-4 whitespace-nowrap" data-test-id={`so-status-${so.id}`}>
        <div className="flex flex-row flex-wrap gap-2 items-center">
          <SalesOrderStatusBadge status={so.status} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" />

          {so.status === 'Draft' && canWrite && (
            <button
              data-test-id={`so-mark-sent-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAs(so, 'Sent');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
            >
              <Send className="h-3.5 w-3.5" />
              {t('salesOrder.list.markAsSent')}
            </button>
          )}

          {so.status === 'Sent' && canWrite && (
            <button
              data-test-id={`so-mark-delivered-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onMarkAs(so, 'Delivered');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors border border-emerald-200"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t('salesOrder.list.markAsDelivered')}
            </button>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap" data-test-id={`so-payment-status-${so.id}`}>
        <div className="flex flex-row flex-wrap gap-2 items-center">
          {canWrite && paymentStatus !== 'unpaid' ? (
            <button
              type="button"
              data-test-id={`so-payment-badge-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onPay(so);
              }}
              title={t('salesOrder.payment.edit')}
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              <SalesOrderPaymentBadge
                paymentStatus={paymentStatus}
                className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer transition-opacity hover:opacity-80"
              />
            </button>
          ) : (
            <SalesOrderPaymentBadge
              paymentStatus={paymentStatus}
              className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
            />
          )}

          {canWrite && paymentStatus === 'unpaid' && so.status !== 'Cancelled' && (
            <button
              data-test-id={`so-mark-paid-${so.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onPay(so, 'paid');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors border border-primary-200"
            >
              <Wallet className="h-3.5 w-3.5" />
              {t('salesOrder.payment.markAsPaid')}
            </button>
          )}
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{so.internalNotes || '-'}</td>
    </tr>
  );
}
